import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Chip, Paper, Divider, Button, TextField, IconButton} from 'views/components';
import {DeleteForever} from 'views/icons';
import {Translate, translate} from 'views/module';

const LanguageEdit = (props) => {
  const DEFAULT_LANGUAGE_DETAIL = {name: '', fluency: ''};

  const {bioQuestions, setFields, setIsComplete} = props;

  const [languages, setLanguages] = React.useState([]);
  const [languagesSubsectionState, setLanguagesSubsectionState] = React.useState({state: 'CHIP', isLanguageSelectedOther: false});
  const [languageDetails, setLanguageDetails] = React.useState(DEFAULT_LANGUAGE_DETAIL);
  const [isFormValid, setIsFormValid] = React.useState(false);

  const availableLanguages = translate('caregiver.CaregiverBiography.Languages.AvailableLanguages');
  const fluencyOptionsMap = translate('caregiver.CaregiverBiography.Languages.Fluency');
  const languageKeys = Object.keys(availableLanguages || {});
  const fluencyOptions = Object.keys(fluencyOptionsMap || {});
  const OTHER_KEY = 'Other';
  const otherLabel = translate('caregiver.CaregiverBiography.Languages.AvailableLanguages.Other');

  // Initialize from bioQuestions
  React.useEffect(() => {
    const sourceLanguages = bioQuestions?.languages || [];
    setLanguages(sourceLanguages);
  }, [bioQuestions]);

  // Sync languages back to parent
  React.useEffect(() => {
    setFields((prev) => ({...prev, languages}));
  }, [languages, setFields]);

  // Completion state (at least one saved language)
  React.useEffect(() => {
    setIsComplete(languages.length > 0);
  }, [languages, setIsComplete]);

  // Keep form validity in sync
  React.useEffect(() => {
    setIsFormValid(Boolean(languageDetails.name.trim() && languageDetails.fluency));
  }, [languageDetails]);

  const handleLanguageClick = (languageKey) => {
    if (languageKey === OTHER_KEY) {
      setLanguageDetails({name: '', fluency: ''});
      setLanguagesSubsectionState({state: 'FORM', isLanguageSelectedOther: true});
    } else {
      setLanguageDetails({name: languageKey, fluency: ''});
      setLanguagesSubsectionState({state: 'FORM', isLanguageSelectedOther: false});
    }
  };

  const handleSaveLanguage = () => {
    const trimmedName = languageDetails.name.trim();
    if (!trimmedName || !languageDetails.fluency) return;

    setLanguages((prev) => {
      const filtered = prev.filter((l) => l.name !== trimmedName);
      return [...filtered, {...languageDetails, name: trimmedName}];
    });

    resetForm();
  };

  const handleRemoveLanguage = (languageName) => {
    if (!languageName) return;
    setLanguages((prev) => prev.filter((l) => l.name !== languageName));
    resetForm();
  };

  const handleFluencyChange = (fluency) => {
    setLanguageDetails((prev) => {
      const next = {...prev, fluency};

      // Auto-save when not "Other"
      if (next.name && !languagesSubsectionState.isLanguageSelectedOther) {
        setLanguages((prevLangs) => {
          const filtered = prevLangs.filter((l) => l.name !== next.name);
          return [...filtered, next];
        });
        resetForm();
      }

      return next;
    });
  };

  const resetForm = () => {
    setLanguagesSubsectionState({state: 'CHIP', isLanguageSelectedOther: false});
    setLanguageDetails(DEFAULT_LANGUAGE_DETAIL);
  };

  const getLanguageLabel = (key) => {
    const label = translate(`caregiver.CaregiverBiography.Languages.AvailableLanguages.${key}`);
    return label !== 'Missing translation' ? label : key;
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" textAlign="center">
        <Translate expression="caregiver.CaregiverBiography.Languages.Title" />
      </Typography>

      {
        languagesSubsectionState.state === 'CHIP' &&
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={1.5}>
          {
            languageKeys.map((languageKey) => {
              const isSelected = languages.some((l) => l.name === languageKey);
              return (
                <Chip
                  key={languageKey}
                  label={getLanguageLabel(languageKey)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  onClick={() => handleLanguageClick(languageKey)}
                  sx={{minWidth: 120, fontWeight: isSelected ? 600 : 500}}
                />
              );
            })
          }
        </Box>
      }

      {
        languagesSubsectionState.state === 'FORM' &&
        <Paper sx={{p: 3, borderRadius: 2}}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
              {
                languagesSubsectionState.isLanguageSelectedOther ?
                otherLabel :
                getLanguageLabel(languageDetails.name)
              }
            </Typography>

            <Divider />

            {
              languagesSubsectionState.isLanguageSelectedOther && (
                <TextField
                  label={translate('caregiver.CaregiverBiography.Languages.LanguagesOther.Title')}
                  placeholder={translate('caregiver.CaregiverBiography.Languages.LanguagesOther.Placeholder')}
                  value={languageDetails.name}
                  onChange={(value) => setLanguageDetails((prev) => ({...prev, name: value}))}
                  fullWidth
                />
              )
            }

            <Typography variant="body2" color="text.secondary">
              <Translate expression="caregiver.CaregiverBiography.Languages.FluencyHelpText" />
            </Typography>

            <Box display="flex" flexWrap="wrap" gap={1}>
              {
                fluencyOptions.map((optionKey) => {
                  const label = translate(`caregiver.CaregiverBiography.Languages.Fluency.${optionKey}`);
                  const isActive = languageDetails.fluency === optionKey;
                  return (
                    <Chip
                      key={optionKey}
                      label={label}
                      color={isActive ? 'primary' : 'default'}
                      variant={isActive ? 'filled' : 'outlined'}
                      onClick={() => handleFluencyChange(optionKey)}
                      sx={{fontWeight: isActive ? 600 : 500}}
                    />
                  );
                })
              }
            </Box>

            <Divider />

            <Stack direction={{xs: 'column', sm: 'row'}} spacing={1.5} justifyContent="space-between">
              <Button
                color="error"
                variant="outlined"
                onClick={() => handleRemoveLanguage(languageDetails.name)}
                label={translate('caregiver.CaregiverBiography.Languages.Action.Remove')}
                sx={{minWidth: 140}}
              />
              {
                languagesSubsectionState.isLanguageSelectedOther &&
                <Button
                  color="primary"
                  onClick={handleSaveLanguage}
                  disabled={!isFormValid}
                  label={translate('caregiver.CaregiverBiography.Languages.Action.Add')}
                  sx={{minWidth: 140}}
                />
              }
            </Stack>
          </Stack>
        </Paper>
      }

      {
        languages.length > 0 &&
        <Paper sx={{p: 2.5, borderRadius: 2}}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
              <Translate
                expression="caregiver.CaregiverBiography.Languages.LanguagesSelectedCount"
                substitute={{count: languages.length}}
              />
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {
                languages.map((language) => {
                  const translationAttempt = translate(`caregiver.CaregiverBiography.Languages.AvailableLanguages.${language.name}`);
                  const languageLabel = translationAttempt !== 'Missing translation' ? translationAttempt : language.name;
                  const fluencyLabel = translate( `caregiver.CaregiverBiography.Languages.Fluency.${language.fluency}`,
                  );
                  return (
                    <Box
                      key={`${language.name}-${language.fluency}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px solid #0288d1',
                        borderRadius: '16px',
                        backgroundColor: '#e1f5fe',
                        paddingY: 0.75,
                        paddingX: 1.25,
                        gap: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{color: '#0288d1', fontWeight: 500}}>
                        {`${languageLabel} : ${fluencyLabel}`}
                      </Typography>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleRemoveLanguage(language.name)}
                      >
                        <DeleteForever fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })
              }
            </Box>
          </Stack>
        </Paper>
      }
    </Stack>
  );
};

LanguageEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
};

export default React.memo(LanguageEdit);
